import { ChangeEvent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import type { Theme } from '@mui/material/styles';

import DropdownSplitButton, {
  type MenuOptionType,
} from 'src/components/DropdownSplitButton';
import ConfirmModal from 'src/components/ConfirmModal';
import ErrorModal from 'src/components/ErrorModal/ErrorModal';
import PageLayout from 'src/components/PageLayout';
import ProgressBar from 'src/components/ProgressBar';
import ToggleSwitch from 'src/components/ToggleSwitch';
import Typography from '@mui/material/Typography';

import messages from './messages';
import { useImportJobsFlow } from './importJobsFlow';
import { useDownloadProjectJobs, useGetProject } from './api';
import JobList from './components/JobList';
import SummaryPanel from './components/SummaryPanel';
import JobForm from './components/JobForm';
import DailyBriefings from './components/DailyBriefings';
import { Styled } from './components/DailyBriefings/index.styled';
import jobFormMessages from './components/JobForm/messages';

type ToggleSwitchType = 'Job List' | 'Daily Briefings';

const buttonBorderRadiusSx = {
  borderRadius: (theme: Theme) => theme.shape.borderRadius,
};
const importButtonColorBySeverity = {
  warning: 'warning',
  error: 'error',
} as const;

export default function ProjectDetailsPage() {
  const { formatMessage } = useIntl();
  const { uuid } = useParams<{ uuid: string }>();

  const [selected, setSelected] = useState<ToggleSwitchType>('Job List');

  if (!uuid) {
    return null;
  }

  const { data: project } = useGetProject(uuid, { enabled: true });
  const { downloadJobs, isDownloading } = useDownloadProjectJobs(
    String(project?.id ?? ''),
    uuid,
  );
  const importJobsFlow = useImportJobsFlow({ projectUuid: uuid });

  const handleDownloadJobs = useCallback(() => {
    downloadJobs();
  }, [downloadJobs]);

  const handleUploadJobs = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      e.target.value = '';

      if (!file) {
        return;
      }

      importJobsFlow.startImport(file);
    },
    [importJobsFlow.startImport],
  );

  const isJobListSelected = selected === 'Job List';
  const importActions: Array<MenuOptionType> = [
    {
      label: formatMessage(messages.latestImportResult),
      onSelect: importJobsFlow.openLastImportReport,
    },
  ];
  const importButtonColor = importJobsFlow.lastImportReportSeverity
    ? importButtonColorBySeverity[importJobsFlow.lastImportReportSeverity]
    : undefined;

  if (!project) {
    return null;
  }

  return (
    <PageLayout backHref="/projects">
      <Grid container spacing={3} width="100%">
        <Grid size={12}>
          <SummaryPanel
            title={project.name}
            projectId={project.id}
            cities={project.cities}
            endDate={project.endDate}
            projectUuid={project.uuid}
            status={project.statusName}
            startDate={project.startDate}
            company={project.customer.name}
            slotsCount={project.totalPublished.slotsCount}
            bookedStaffCount={project.totalPublished.bookedCount}
            isPriority={project.isPriority && project.statusName === 'active'}
          />
        </Grid>

        <Grid size={6}>
          <ToggleSwitch
            selected={selected}
            onChange={setSelected}
            options={[
              formatMessage(messages.jobListSwitchLabel),
              formatMessage(messages.dailyBriefingSwitchLabel),
            ]}
          />
        </Grid>

        <Grid size={6} display="flex" justifyContent="flex-end" gap={4}>
          {isJobListSelected && (
            <Grid display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={handleDownloadJobs}
                startIcon={<FileDownloadOutlinedIcon color="inherit" />}
                sx={buttonBorderRadiusSx}
                disabled={isDownloading}
              >
                {formatMessage(jobFormMessages.downloadButton)}
              </Button>
              <DropdownSplitButton
                severity={importJobsFlow.lastImportReportSeverity ?? undefined}
                disabled={importJobsFlow.isImportingJobs}
                options={importActions}
                button={() => (
                  <Button
                    color={importButtonColor}
                    variant="outlined"
                    component="label"
                    startIcon={<FileUploadOutlinedIcon color="inherit" />}
                    sx={buttonBorderRadiusSx}
                    disabled={importJobsFlow.isImportingJobs}
                  >
                    {formatMessage(jobFormMessages.uploadButton)}
                    <Styled.VisuallyHiddenInput
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleUploadJobs}
                    />
                  </Button>
                )}
              />
            </Grid>
          )}
          <JobForm projectUuid={project.uuid} isCreate />
        </Grid>

        <Grid size={12}>
          {isJobListSelected ? (
            <JobList projectUuid={project.uuid} />
          ) : (
            <DailyBriefings projectUuid={project.uuid} />
          )}
        </Grid>
      </Grid>

      <ErrorModal
        errorResponse={importJobsFlow.modalError}
        onConfirm={
          importJobsFlow.isForceImportAvailable
            ? importJobsFlow.confirmForceImport
            : undefined
        }
        onCancel={importJobsFlow.closeImportResultModal}
        onDownload={
          importJobsFlow.isFailedRowsFileAvailable
            ? importJobsFlow.downloadFailedRows
            : undefined
        }
      />
      <ConfirmModal
        open={importJobsFlow.isImportResultEmptyStateOpen}
        onClose={importJobsFlow.closeImportResultModal}
        title={formatMessage(messages.latestImportResult)}
        disableBackdropPress={false}
      >
        <Typography>
          {formatMessage(messages.latestImportResultEmptyState)}
        </Typography>
      </ConfirmModal>
      <ProgressBar percent={importJobsFlow.importProgressPercent} />
    </PageLayout>
  );
}
